// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type {
  CreateCurbAndGutterBreaklineData,
  CreateCurbAndGutterBreaklineErrors,
  CreateCurbAndGutterBreaklineResponses,
  CreateJerseyBarrierBreaklineData,
  CreateJerseyBarrierBreaklineErrors,
  CreateJerseyBarrierBreaklineResponses,
  CreateRoadCrownBreaklineData,
  CreateRoadCrownBreaklineErrors,
  CreateRoadCrownBreaklineResponses,
  CreateWorkspaceData,
  CreateWorkspaceErrors,
  CreateWorkspaceResponses,
  DeleteCurbAndGutterBreaklineData,
  DeleteCurbAndGutterBreaklineErrors,
  DeleteCurbAndGutterBreaklineResponses,
  DeleteJerseyBarrierBreaklineData,
  DeleteJerseyBarrierBreaklineErrors,
  DeleteJerseyBarrierBreaklineResponses,
  DeleteReferenceData,
  DeleteReferenceErrors,
  DeleteReferenceResponses,
  DeleteRoadCrownBreaklineData,
  DeleteRoadCrownBreaklineErrors,
  DeleteRoadCrownBreaklineResponses,
  DeleteWorkspaceData,
  DeleteWorkspaceErrors,
  DeleteWorkspaceResponses,
  GetCurbAndGutterBreaklineData,
  GetCurbAndGutterBreaklineErrors,
  GetCurbAndGutterBreaklineResponses,
  GetJerseyBarrierBreaklineData,
  GetJerseyBarrierBreaklineErrors,
  GetJerseyBarrierBreaklineResponses,
  GetRoadCrownBreaklineData,
  GetRoadCrownBreaklineErrors,
  GetRoadCrownBreaklineResponses,
  GetWorkspaceData,
  GetWorkspaceErrors,
  GetWorkspaceResponses,
  HealthCheckData,
  HealthCheckErrors,
  HealthCheckResponses,
  ListBreaklinesData,
  ListBreaklinesErrors,
  ListBreaklinesResponses,
  ListReferencesData,
  ListReferencesErrors,
  ListReferencesResponses,
  ListWorkspacesData,
  ListWorkspacesErrors,
  ListWorkspacesResponses,
  SetMobileMappingMissionReferenceData,
  SetMobileMappingMissionReferenceErrors,
  SetMobileMappingMissionReferenceResponses,
  UpdateWorkspaceData,
  UpdateWorkspaceErrors,
  UpdateWorkspaceResponses,
} from './types.gen';

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = Options2<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Health check endpoint
 *
 * Returns dependency health. Responds with 503 if any check is unhealthy.
 */
export const healthCheck = <ThrowOnError extends boolean = false>(
  options?: Options<HealthCheckData, ThrowOnError>
) =>
  (options?.client ?? client).get<
    HealthCheckResponses,
    HealthCheckErrors,
    ThrowOnError
  >({ url: '/health', ...options });

/**
 * List workspaces for a tenant
 *
 * ### List workspaces
 * Returns a paginated list of all FX workspaces in the tenant.
 *
 * ### Notes
 * - Use **pageIndex** and **pageSize** to paginate.
 * - Each item is a full workspace (including tenant TRN, workspace TRN, name, and audit fields).
 */
export const listWorkspaces = <ThrowOnError extends boolean = false>(
  options: Options<ListWorkspacesData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListWorkspacesResponses,
    ListWorkspacesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces',
    ...options,
  });

/**
 * Create a workspace
 *
 * ### Create a workspace
 * Creates a new FX workspace in the tenant.
 *
 * ### Request body
 * - **name** (required): Display name of the workspace. Must be non-empty.
 *
 * ### Notes
 * - The response returns the created workspace, including the workspace TRN. Use that TRN as the `workspaceTrn` path parameter for subsequent calls.
 * - The workspace starts empty. Add references via the type-specific endpoints (e.g. `PUT .../references/mobileMappingMission`).
 */
export const createWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<CreateWorkspaceData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateWorkspaceResponses,
    CreateWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete a workspace
 *
 * ### Delete a workspace
 * Permanently deletes the workspace. All WIP data owned by the workspace (settings, unpublished FX results,
 * workspace-scoped geometries) is deleted.
 *
 * ### Notes
 * - **Referenced business objects are not affected** by this operation.
 * - **Published FX results are not affected** by this operation; they continue to exist as independent BOs in Connect.
 */
export const deleteWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<DeleteWorkspaceData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteWorkspaceResponses,
    DeleteWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}',
    ...options,
  });

/**
 * Get a workspace by TRN
 *
 * ### Get a workspace
 * Returns a single workspace by its TRN.
 */
export const getWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<GetWorkspaceData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetWorkspaceResponses,
    GetWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}',
    ...options,
  });

/**
 * Update a workspace
 *
 * ### Update a workspace
 * Updates the workspace's metadata. Only provided fields are updated (partial update).
 *
 * ### Notes
 * - Returns the updated workspace.
 */
export const updateWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<UpdateWorkspaceData, ThrowOnError>
) =>
  (options.client ?? client).patch<
    UpdateWorkspaceResponses,
    UpdateWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List references in a workspace
 *
 * ### List references
 * Returns a paginated list of all references the workspace holds to external business objects
 * (e.g. Mobile Mapping missions, terrestrial scans, Connect models).
 *
 * ### Notes
 * - Each item identifies the referenced BO by its TRN; the workspace does not own the referenced data.
 * - Use **pageIndex** and **pageSize** to paginate.
 */
export const listReferences = <ThrowOnError extends boolean = false>(
  options: Options<ListReferencesData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListReferencesResponses,
    ListReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references',
    ...options,
  });

/**
 * Set the mobile mapping mission reference
 *
 * ### Set mobile mapping mission reference
 * Sets the workspace's reference to an existing Mobile Mapping mission. A workspace holds at most one Mobile Mapping mission reference;
 * this call creates it or replaces the existing one. The referenced mission is not modified or copied.
 *
 * ### Request body
 * - **refTrn** (required): TRN of the existing Mobile Mapping mission to reference.
 *
 * ### Notes
 * - The caller must have permission to read the referenced mission in Connect.
 * - This operation is idempotent: setting the same reference again returns **200 OK** and does not create a duplicate.
 */
export const setMobileMappingMissionReference = <
  ThrowOnError extends boolean = false,
>(
  options: Options<SetMobileMappingMissionReferenceData, ThrowOnError>
) =>
  (options.client ?? client).put<
    SetMobileMappingMissionReferenceResponses,
    SetMobileMappingMissionReferenceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/mobileMappingMission',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Remove a reference to an external business object
 *
 * ### Delete reference
 * Removes the workspace's reference to the specified external business object. The referenced BO itself is **not** deleted.
 *
 * ### Notes
 * - If the reference does not exist, the API returns **404 Not Found**.
 */
export const deleteReference = <ThrowOnError extends boolean = false>(
  options: Options<DeleteReferenceData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteReferenceResponses,
    DeleteReferenceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/{refTrn}',
    ...options,
  });

/**
 * List breaklines
 *
 * Returns all breaklines in the workspace across every type.
 */
export const listBreaklines = <ThrowOnError extends boolean = false>(
  options: Options<ListBreaklinesData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListBreaklinesResponses,
    ListBreaklinesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines',
    ...options,
  });

/**
 * Delete the curb and gutter breakline
 *
 * Deletes the curb and gutter breakline and its Maps API layer.
 */
export const deleteCurbAndGutterBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<DeleteCurbAndGutterBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteCurbAndGutterBreaklineResponses,
    DeleteCurbAndGutterBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/curbAndGutter',
    ...options,
  });

/**
 * Get the curb and gutter breakline
 *
 * Returns the workspace's curb and gutter breakline, including its Maps API layer.
 */
export const getCurbAndGutterBreakline = <ThrowOnError extends boolean = false>(
  options: Options<GetCurbAndGutterBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetCurbAndGutterBreaklineResponses,
    GetCurbAndGutterBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/curbAndGutter',
    ...options,
  });

/**
 * Create the curb and gutter breakline
 *
 * ### Create the curb and gutter breakline
 * Creates the curb and gutter breakline and provisions its Maps API layer with the
 * correct schema and style.
 *
 * Only one curb and gutter breakline can exist per workspace; a second create returns
 * **409 Conflict**.
 */
export const createCurbAndGutterBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<CreateCurbAndGutterBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateCurbAndGutterBreaklineResponses,
    CreateCurbAndGutterBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/curbAndGutter',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete the road crown breakline
 *
 * Deletes the road crown breakline and its Maps API layer.
 */
export const deleteRoadCrownBreakline = <ThrowOnError extends boolean = false>(
  options: Options<DeleteRoadCrownBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteRoadCrownBreaklineResponses,
    DeleteRoadCrownBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/roadCrown',
    ...options,
  });

/**
 * Get the road crown breakline
 *
 * Returns the workspace's road crown breakline, including its Maps API layer.
 */
export const getRoadCrownBreakline = <ThrowOnError extends boolean = false>(
  options: Options<GetRoadCrownBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetRoadCrownBreaklineResponses,
    GetRoadCrownBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/roadCrown',
    ...options,
  });

/**
 * Create the road crown breakline
 *
 * ### Create the road crown breakline
 * Creates the road crown breakline and provisions its Maps API layer with the
 * correct schema and style.
 *
 * Only one road crown breakline can exist per workspace; a second create returns
 * **409 Conflict**.
 */
export const createRoadCrownBreakline = <ThrowOnError extends boolean = false>(
  options: Options<CreateRoadCrownBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateRoadCrownBreaklineResponses,
    CreateRoadCrownBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/roadCrown',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete the jersey barrier breakline
 *
 * Deletes the jersey barrier breakline and its Maps API layer.
 */
export const deleteJerseyBarrierBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<DeleteJerseyBarrierBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteJerseyBarrierBreaklineResponses,
    DeleteJerseyBarrierBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/jerseyBarrier',
    ...options,
  });

/**
 * Get the jersey barrier breakline
 *
 * Returns the workspace's jersey barrier breakline, including its Maps API layer.
 */
export const getJerseyBarrierBreakline = <ThrowOnError extends boolean = false>(
  options: Options<GetJerseyBarrierBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetJerseyBarrierBreaklineResponses,
    GetJerseyBarrierBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/jerseyBarrier',
    ...options,
  });

/**
 * Create the jersey barrier breakline
 *
 * ### Create the jersey barrier breakline
 * Creates the jersey barrier breakline and provisions its Maps API layer with the
 * correct schema and style.
 *
 * Only one jersey barrier breakline can exist per workspace; a second create returns
 * **409 Conflict**.
 */
export const createJerseyBarrierBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<CreateJerseyBarrierBreaklineData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateJerseyBarrierBreaklineResponses,
    CreateJerseyBarrierBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/jerseyBarrier',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
